import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ScienceDebugger, DiagnosticStep } from './ScienceDebugger';

export const RoutingScience: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const routingSteps: DiagnosticStep[] = [
    {
      id: 'react-router-loaded',
      label: 'React Router is loaded',
      category: 'Core',
      critical: true,
      check: () => ({
        passed: typeof navigate === 'function' && location !== undefined,
        details: {
          hasNavigate: typeof navigate === 'function',
          hasLocation: location !== undefined,
          currentPath: location?.pathname,
        }
      })
    },
    {
      id: 'current-location',
      label: 'Current location is valid',
      category: 'Core',
      check: () => ({
        passed: location.pathname.startsWith('/'),
        details: {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
          state: location.state,
        }
      })
    },
    {
      id: 'history-available',
      label: 'Browser history API available',
      category: 'Browser',
      check: () => ({
        passed: 'history' in window && typeof window.history.pushState === 'function',
        details: {
          historyLength: window.history.length,
          hasPushState: typeof window.history.pushState === 'function',
          hasReplaceState: typeof window.history.replaceState === 'function',
        }
      })
    },
    {
      id: 'route-params',
      label: 'Route parameters extractable',
      category: 'Parsing',
      check: () => {
        const params = new URLSearchParams(location.search);
        return {
          passed: true,
          details: {
            paramCount: params.toString() ? Array.from(params.entries()).length : 0,
            params: Object.fromEntries(params.entries()),
          }
        };
      }
    },
    {
      id: 'known-routes',
      label: 'Check known routes exist',
      category: 'Routes',
      check: async () => {
        const knownRoutes = [
          '/',
          '/whatsapp-templates',
          '/zappilot',
          '/biig-links',
          '/projects',
          '/dev',
        ];

        const results = await Promise.all(
          knownRoutes.map(async (route) => {
            try {
              const response = await fetch(`${window.location.origin}${route}`, {
                method: 'HEAD',
              });
              return { route, exists: response.ok };
            } catch {
              return { route, exists: false };
            }
          })
        );

        const allExist = results.every(r => r.exists);
        return {
          passed: allExist,
          details: {
            routes: results,
            totalRoutes: knownRoutes.length,
            accessibleRoutes: results.filter(r => r.exists).length,
          }
        };
      }
    },
    {
      id: 'navigation-test',
      label: 'Navigation function works',
      category: 'Navigation',
      check: () => {
        try {
          // Test that navigate function exists and is callable
          const canNavigate = typeof navigate === 'function';
          return {
            passed: canNavigate,
            details: {
              navigateType: typeof navigate,
              currentPath: location.pathname,
            }
          };
        } catch (error) {
          return {
            passed: false,
            error: error instanceof Error ? error.message : 'Navigation test failed',
          };
        }
      }
    },
    {
      id: 'base-url',
      label: 'Base URL configuration correct',
      category: 'Configuration',
      check: () => {
        const base = document.querySelector('base');
        const hasBase = base !== null;
        return {
          passed: true, // Base is optional
          details: {
            hasBaseTag: hasBase,
            baseHref: base?.href,
            documentURL: document.URL,
            origin: window.location.origin,
          }
        };
      }
    },
    {
      id: 'route-guards',
      label: 'Check protected routes',
      category: 'Security',
      check: () => {
        // Check if auth-protected routes redirect properly
        const protectedRoutes = [
          '/admin',
          '/settings',
          '/profile',
        ];

        return {
          passed: true, // This is informational
          details: {
            protectedRoutes,
            currentPath: location.pathname,
            isProtectedRoute: protectedRoutes.includes(location.pathname),
          }
        };
      }
    },
    {
      id: 'scroll-restoration',
      label: 'Scroll restoration configured',
      category: 'UX',
      check: () => ({
        passed: true,
        details: {
          scrollY: window.scrollY,
          scrollX: window.scrollX,
          historyScrollRestoration: window.history.scrollRestoration,
        }
      })
    },
    {
      id: 'popstate-listener',
      label: 'Browser back/forward detection',
      category: 'Browser',
      check: () => {
        let hasListener = false;
        try {
          // Check if we can add a popstate listener
          const testListener = () => {};
          window.addEventListener('popstate', testListener);
          window.removeEventListener('popstate', testListener);
          hasListener = true;
        } catch {}

        return {
          passed: hasListener,
          details: {
            canAddListener: hasListener,
            historyLength: window.history.length,
          }
        };
      }
    },
  ];

  return (
    <ScienceDebugger
      title="🧭 Routing Science"
      description="Diagnose React Router and navigation issues"
      steps={routingSteps}
      groupByCategory={true}
      animationDelay={200}
    />
  );
};